/**
 * Convite de instalação do PWA (Android/desktop).
 *
 * O `beforeinstallprompt` dispara uma vez só, muito cedo — antes de qualquer
 * tela estar montada. Por isso quem escuta é o módulo, no boot do app: se o
 * ouvinte vivesse dentro de um componente, o evento já teria passado quando ele
 * montasse e não haveria como abrir o instalador nunca mais.
 *
 * O estado fica aqui, num observable, e não na Vuex: aquele store é persistido
 * e o evento não sobrevive a serialização — justo no cold start, que é o
 * momento em que o convite existe.
 */
import Vue from "vue";
import { isStandalone } from "./webPush";

const state = Vue.observable({
  deferredPrompt: null,
  installed: false,
});

let initialized = false;

function track(eventName, props = {}) {
  (async () => {
    try {
      const { trackEvent } = await import("@/services/posthog");
      trackEvent(eventName, props);
    } catch (_) {
      /* posthog opcional */
    }
  })();
}

export function initPwaInstall() {
  if (initialized) return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    // Sem isto o Chrome desenha a mini-infobar dele, e passam a ser dois
    // convites disputando o mesmo clique.
    event.preventDefault();
    state.deferredPrompt = event;
  });

  window.addEventListener("appinstalled", () => {
    state.installed = true;
    state.deferredPrompt = null;
    track("pwa_installed");
  });
}

/** Dá para oferecer a instalação agora? */
export function canPromptInstall() {
  if (state.installed) return false;
  if (isStandalone()) return false;
  return !!state.deferredPrompt;
}

/**
 * Abre o instalador do navegador e devolve a escolha ("accepted"/"dismissed"),
 * ou null se não havia convite guardado.
 */
export async function promptInstall() {
  const event = state.deferredPrompt;
  if (!event) return null;

  // O evento é de uso único: o navegador não o entrega de novo depois do
  // prompt(). Descartar antes de esperar a escolha evita um segundo clique
  // num botão que já não faz nada.
  state.deferredPrompt = null;

  event.prompt();
  const choice = await event.userChoice;
  return (choice && choice.outcome) || null;
}
